import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import SectionHeader from "../../../../util/component/SectionHeader";
import {useProcurerActions} from "../../service/useProcurerActions";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import {Button, Grid, MenuItem, Paper, TextField} from "@mui/material";
import ProcurerAccordionDetail from "./modals/ProcurerAccordionDetail";
import ProcurerUpdateModal from "./modals/ProcurerUpdateModal";
import {useStatusValuesProcurer} from "../../component/useStatusValuesProcurer";
import {PROCURER_STATUS_DISMISSED, PROCURER_STATUS_READY} from "../../component/Constants";
import {
    UserLegalEntitiesOrDealersInputGroup
} from "../../component/UserLegalEntitiesOrDealersInputGroup/UserLegalEntitiesOrDealersInputGroup";

const ProcurerUpdatePage = () => {
    const { t } = useTranslation(['salesmarketing', 'translation']);
    const { getStatusLabelProcurer } = useStatusValuesProcurer();
    const navigate = useNavigate();
    const { procurerId } = useParams();
    const [modalOpen, setModalOpen] = useState(false);
    const [detail, setDetail] = useState({
        tenantId: "",
        dealerId: "",
        genVendorCode: "",
        status: "",
        commercialCode: "",
        naming: "",
        bpCode: "",
        serviceType: [] as string[],
        country: "",
        region: "",
        city: "",
        address: "",
        streetNumber: "",
        postalCode: "",
        province: "",
        phoneNumber: "",
        cellPhoneNumber: "",
        email: "",
        area: "",
        finOldIdentificationCode: "",
        dms: "",
        agreementDate: "",
        repCompanyName: "",
        repVatNumber: "",
        siren: "",
        siret: "",
        representativeName: "",
        representativeEmail: "",
        representativeCellphone: "",
        taxCode: "",
        sdiCode: "",
        representativeContactName: "",
        representativeContactEmail: "",
        representativeContactCellphone: "",
        representativeIban: "",
        representativeAddress: "",
        representativeStreetNumber: "",
        representativePostalCode: "",
        representativeCity: "",
        representativeProvince: "",
        representativeHqCellphone: "",
        representativePec: "",
        oldCode:"",
        affIncomeCode:""
    });
    const procurerActions = useProcurerActions();

    const { handleSubmit, control, reset } = useForm({
        defaultValues: {
            status: detail?.status,
            commercialCode: detail?.commercialCode
        },
        resolver: yupResolver(Yup.object({
            status: Yup.string().nullable(),
            commercialCode: Yup.string().nullable()
        }))
    });

    const fetchDetail = async (procurerId: any) => {
        let res = await procurerActions.detailProcurer(procurerId as string);
        setDetail(res);
        reset(res);
    }

    useEffect(() => {
        fetchDetail(procurerId);
    }, [])

    const onSubmit = async (data: any) => {
        data.genBpCode = procurerId;
        await procurerActions.updateProcurer(data);
        setModalOpen(true);
    }

    const statuses = [
        {
            key: PROCURER_STATUS_READY,
            value: getStatusLabelProcurer(PROCURER_STATUS_READY)
        },
        {
            key: PROCURER_STATUS_DISMISSED,
            value: getStatusLabelProcurer(PROCURER_STATUS_DISMISSED)
        }
    ]

    return (
        <>
            <SectionHeader title={t('salesmarketing:procurer.updateHeader.title')} subtitle={t('salesmarketing:procurer.updateHeader.subTitle')} />
            <Paper elevation={0} sx={{ padding: "30px" }}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Grid container spacing={2}>
                        <UserLegalEntitiesOrDealersInputGroup
                            tenantId={detail.tenantId}
                            dealerId={detail.dealerId}
                            disabled={!!procurerId}
                        />
                        <Grid container item xs={12} spacing={1}>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:procurer.detailContent.companyNameField')}
                                    disabled
                                    value={detail?.repCompanyName}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:procurer.detailContent.vatNumberField')}
                                    disabled
                                    value={detail?.repVatNumber}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:procurer.detailContent.sirenField')}
                                    disabled
                                    value={detail?.siren}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:procurer.detailContent.siretField')}
                                    disabled
                                    value={detail?.siret}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:procurer.detailContent.vendorCodeField')}
                                    disabled
                                    value={detail?.genVendorCode}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:procurer.detailContent.bpCodeField')}
                                    disabled
                                    value={detail?.bpCode}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Controller
                                    control={control}
                                    name={"status"}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            fullWidth
                                            label={t('salesmarketing:procurer.detailContent.statusField')}
                                            select
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        >
                                            {statuses?.map((s) => (
                                                <MenuItem key={s.key} value={s.key}>
                                                    {s.value}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )} />
                            </Grid>
                            <Grid item xs={3}>
                                <Controller
                                    control={control}
                                    name={"commercialCode"}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            fullWidth
                                            label={t('salesmarketing:procurer.detailContent.commercialCodeField')}
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )} />
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <ProcurerAccordionDetail detail={detail} isDisabled={true} />
                        </Grid>
                        <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                            <Grid container item xs={12} rowSpacing={1} columnSpacing={3}
                                  justifyContent={"flex-end"}>
                                <Grid item>
                                    <Button fullWidth variant="outlined" size="small" onClick={() => { navigate("/salesmarketing/intermediarylist") }}>
                                        {t('salesmarketing:procurer.buttons.cancel')}
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button fullWidth color="accent" size="small" type="submit">{t('salesmarketing:procurer.buttons.confirm')}</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <ProcurerUpdateModal open={modalOpen} setOpen={setModalOpen} />
        </>
    );
}


export default ProcurerUpdatePage;